import { useState, useRef, useEffect } from 'react'
import './DatePicker.css'

const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
const DAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfWeek(year, month) {
  const d = new Date(year, month, 1).getDay()
  return d === 0 ? 6 : d - 1 // Mon=0 .. Sun=6
}

export default function DatePicker({ name, defaultValue, value: controlledValue, onChange, required, className, style }) {
  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]

  const parseDate = (str) => {
    if (!str) return { year: today.getFullYear(), month: today.getMonth(), day: today.getDate() }
    const [y, m, d] = str.split('-').map(Number)
    return { year: y, month: m - 1, day: d }
  }

  const initial = parseDate(controlledValue || defaultValue || todayStr)
  const [selectedDate, setSelectedDate] = useState(controlledValue || defaultValue || todayStr)
  const [viewYear, setViewYear] = useState(initial.year)
  const [viewMonth, setViewMonth] = useState(initial.month)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    if (controlledValue !== undefined) setSelectedDate(controlledValue)
  }, [controlledValue])

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const formatDisplay = (dateStr) => {
    if (!dateStr) return ''
    const [y, m, d] = dateStr.split('-').map(Number)
    return `${d} ${MONTHS_RU[m - 1]?.toLowerCase()} ${y}`
  }

  const handleSelect = (day) => {
    const m = String(viewMonth + 1).padStart(2, '0')
    const d = String(day).padStart(2, '0')
    const val = `${viewYear}-${m}-${d}`
    setSelectedDate(val)
    setIsOpen(false)
    onChange?.(val)
  }

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(v => v - 1) }
    else setViewMonth(v => v - 1)
  }

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(v => v + 1) }
    else setViewMonth(v => v + 1)
  }

  const goToday = () => {
    setViewYear(today.getFullYear())
    setViewMonth(today.getMonth())
    handleSelect(today.getDate())
  }

  const daysInMonth = getDaysInMonth(viewYear, viewMonth)
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth)
  const prevDays = getDaysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1, viewMonth === 0 ? viewYear - 1 : viewYear)

  const selectedParsed = parseDate(selectedDate)
  const todayParsed = parseDate(todayStr)

  const cells = []
  // Previous month trailing days
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevDays - i, type: 'prev' })
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, type: 'current' })
  }
  // Next month leading days
  const remaining = 42 - cells.length
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, type: 'next' })
  }
  // Trim to 6 rows max, but only show needed
  const rows = Math.ceil(cells.length / 7)
  const visibleCells = cells.slice(0, rows * 7)

  return (
    <div ref={containerRef} className="custom-datepicker" style={{ position: 'relative', ...style }}>
      {/* Hidden input for form data */}
      <input type="hidden" name={name} value={selectedDate} />

      {/* Display button */}
      <button
        type="button"
        className={`input custom-datepicker-trigger ${className || ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', width: '100%', textAlign: 'left' }}
      >
        <span>{formatDisplay(selectedDate)}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </button>

      {/* Dropdown calendar */}
      {isOpen && (
        <div className="custom-datepicker-dropdown">
          {/* Header */}
          <div className="custom-datepicker-header">
            <button type="button" className="custom-datepicker-nav" onClick={prevMonth}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span className="custom-datepicker-title">{MONTHS_RU[viewMonth]} {viewYear}</span>
            <button type="button" className="custom-datepicker-nav" onClick={nextMonth}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          {/* Weekday headers */}
          <div className="custom-datepicker-weekdays">
            {DAYS_SHORT.map(d => <div key={d} className="custom-datepicker-weekday">{d}</div>)}
          </div>

          {/* Days grid */}
          <div className="custom-datepicker-grid">
            {visibleCells.map((cell, i) => {
              const isCurrent = cell.type === 'current'
              const isSelected = isCurrent && cell.day === selectedParsed.day && viewMonth === selectedParsed.month && viewYear === selectedParsed.year
              const isToday = isCurrent && cell.day === todayParsed.day && viewMonth === todayParsed.month && viewYear === todayParsed.year

              return (
                <button
                  key={i}
                  type="button"
                  className={`custom-datepicker-day${isCurrent ? '' : ' other-month'}${isSelected ? ' selected' : ''}${isToday && !isSelected ? ' today' : ''}`}
                  onClick={() => {
                    if (cell.type === 'prev') { prevMonth(); return }
                    if (cell.type === 'next') { nextMonth(); return }
                    handleSelect(cell.day)
                  }}
                >
                  {cell.day}
                </button>
              )
            })}
          </div>

          {/* Footer */}
          <div className="custom-datepicker-footer">
            <button type="button" className="custom-datepicker-today" onClick={goToday}>Сегодня</button>
          </div>
        </div>
      )}
    </div>
  )
}
